import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Settings, BarChart3, ShieldCheck, Package,
  Bell, Search, ChevronDown, LogOut, ChevronsLeft, ChevronsRight,
  HelpCircle, Sparkles, Menu,
} from 'lucide-react';
import { MODULES } from '@/lib/modules';
import { useAuth } from '@/lib/auth';

/**
 * Reusable dashboard shell — works for both the global Control Panel
 * (super admin) and any module dashboard. Pass { module } to tint the
 * accent stripe; omit for the platform-wide control panel.
 */
export default function DashboardShell({
  module: activeModule = null,
  sectionTitle = 'Overview',
  navItems = [
    { label: 'Overview', icon: LayoutDashboard, to: null },
    { label: 'Team', icon: Users, to: '/team' },
    { label: 'Payments', icon: BarChart3, to: '/payments' },
    { label: 'Settings', icon: Settings, to: '/settings' },
  ],
  children,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const accent = activeModule?.accent || '#F59B2D';
  const brandName = activeModule ? activeModule.short : 'Control Panel';
  const ModuleIcon = activeModule?.icon;

  // Filter module switcher by what the org has activated (super admin sees all)
  const availableModules = user?.role === 'super_admin'
    ? MODULES
    : MODULES.filter((m) => (user?.modules || []).includes(m.slug));

  const initials = (user?.name || 'NX')
    .split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex" data-testid="dashboard-shell">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`bg-navy text-white flex-shrink-0 transition-all duration-300 flex flex-col
          fixed lg:sticky inset-y-0 left-0 z-50 lg:z-auto h-screen top-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${collapsed ? 'lg:w-[72px] w-[260px]' : 'w-[260px]'}`}
        data-testid="sidebar"
      >
        <div className="px-4 py-5 border-b border-white/10 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="sidebar-brand">
            <img src="/logos/attp-main.png" alt="" className="h-9 w-9 object-contain flex-shrink-0" />
            {!collapsed && (
              <div className="leading-tight min-w-0">
                <div className="font-display font-bold tracking-tight truncate">
                  ATTP <span className="text-amber">NEXUS</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 truncate">
                  {brandName}
                </div>
              </div>
            )}
          </Link>
        </div>

        {/* Module switcher */}
        {!collapsed && (
          <div className="px-3 py-3 border-b border-white/10">
            <div className="label-tag text-white/40 mb-2 px-1">Switch Module</div>
            <div className="relative group">
              <button
                className="w-full flex items-center justify-between px-3 py-2 bg-white/5 hover:bg-white/10 rounded-md text-sm border border-white/10 transition-colors"
                data-testid="module-switcher-btn"
              >
                <span className="flex items-center gap-2 truncate">
                  {activeModule ? (
                    <>
                      <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
                      <span className="truncate">{activeModule.short}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-amber" />
                      <span>Platform Control</span>
                    </>
                  )}
                </span>
                <ChevronDown className="w-4 h-4 opacity-60" />
              </button>
              <div className="absolute left-0 right-0 mt-1 bg-navy-700 border border-white/10 rounded-md shadow-xl invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all z-30 max-h-72 overflow-auto">
                <button
                  onClick={() => navigate('/control-panel')}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 flex items-center gap-2"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber" /> Platform Control Panel
                </button>
                <div className="h-px bg-white/10" />
                {availableModules.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => navigate(`/dashboard/${m.slug}`)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 flex items-center gap-2"
                    data-testid={`switch-to-${m.slug}`}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: m.accent }} />
                    <span className="truncate">{m.short}</span>
                  </button>
                ))}
                {availableModules.length === 0 && (
                  <div className="px-3 py-2 text-xs text-white/40">No modules activated yet.</div>
                )}
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 py-4 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map((item, i) => {
            const Icon = item.icon;
            const active = i === 0;
            const Wrap = item.to ? Link : 'a';
            const wrapProps = item.to ? { to: item.to } : { href: '#' };
            return (
              <Wrap
                key={item.label}
                {...wrapProps}
                className={`relative mx-2 my-0.5 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`sidebar-link-${item.label.toLowerCase()}`}
              >
                {active && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r"
                    style={{ background: accent }}
                  />
                )}
                <Icon className="w-4.5 h-4.5 flex-shrink-0" strokeWidth={1.7} />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Wrap>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <button className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition">
            <HelpCircle className="w-4.5 h-4.5" strokeWidth={1.7} />
            {!collapsed && <span>Help & Docs</span>}
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/5 transition" data-testid="sidebar-signout">
            <LogOut className="w-4.5 h-4.5" strokeWidth={1.7} />
            {!collapsed && <span>Sign out</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 rounded-md text-xs text-white/50 hover:text-white/80 hover:bg-white/5 transition"
            data-testid="sidebar-collapse"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /> Collapse</>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header
          className="sticky top-0 z-30 bg-white border-b border-neutral-200"
          data-testid="dashboard-header"
        >
          <div className="px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4 min-w-0">
              {/* Mobile hamburger */}
              <button
                className="lg:hidden p-2 -ml-2 rounded-md hover:bg-neutral-100 text-navy"
                onClick={() => setMobileOpen(true)}
                aria-label="Open sidebar"
                data-testid="mobile-sidebar-toggle"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden md:flex items-center gap-2 text-sm text-neutral-500">
                {activeModule ? (
                  <>
                    <span className="px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider" style={{ background: `${accent}15`, color: accent }}>
                      {ModuleIcon && <ModuleIcon className="w-3 h-3 inline mr-1" />} {activeModule.short}
                    </span>
                    <span className="text-neutral-300">/</span>
                    <span className="text-navy font-medium">{sectionTitle}</span>
                  </>
                ) : (
                  <>
                    <span className="px-2 py-0.5 bg-amber/10 text-amber-600 rounded-sm text-[11px] font-semibold uppercase tracking-wider">
                      Super Admin
                    </span>
                    <span className="text-neutral-300">/</span>
                    <span className="text-navy font-medium">{sectionTitle}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-md w-72 text-sm text-neutral-500">
                <Search className="w-4 h-4" />
                <input
                  className="bg-transparent outline-none flex-1 placeholder:text-neutral-400"
                  placeholder="Search organizations, modules, users…"
                  data-testid="header-search"
                />
                <kbd className="text-[10px] bg-white border border-neutral-200 rounded px-1.5 py-0.5">⌘K</kbd>
              </div>
              <button className="p-2 rounded-md hover:bg-neutral-100 relative" data-testid="header-bell">
                <Bell className="w-5 h-5 text-neutral-600" />
                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber animate-pulse-glow" />
              </button>
              <div className="flex items-center gap-2 pl-2 ml-1 border-l border-neutral-200">
                <div className="h-9 w-9 rounded-md bg-navy text-white flex items-center justify-center font-semibold">
                  {initials}
                </div>
                <div className="hidden sm:block leading-tight pr-2">
                  <div className="text-sm font-medium text-navy truncate max-w-[140px]">{user?.name || '—'}</div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">{user?.role === 'super_admin' ? 'Platform Owner' : (user?.org_name || 'Org user')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Accent stripe */}
          <div className="h-[2px] w-full" style={{ background: `linear-gradient(90deg, ${accent}00, ${accent}, ${accent}00)` }} />
        </header>

        <main className="flex-1 px-6 lg:px-8 py-6">{children}</main>
      </div>
    </div>
  );
}
